import { getRole } from "./roleUtils";

// Sidebar menus a user can be given access to (Users / Settings / Audit Log
// stay admin-only and are not assignable). `paths` are route prefixes.
export const MENU_ACCESS = [
  // Retail
  { key: "dashboard",      label: "Dashboard",       group: "Retail",   paths: ["/dashboard"] },
  { key: "products",       label: "Products",        group: "Retail",   paths: ["/listproduct", "/addproduct", "/editproduct"] },
  { key: "orders",         label: "Orders",          group: "Retail",   paths: ["/orders"] },
  { key: "customers",      label: "Customers",       group: "Retail",   paths: ["/customers"] },
  { key: "stock",          label: "Stock",           group: "Retail",   paths: ["/stock"] },
  { key: "saree-details",  label: "Saree Details",   group: "Retail",   paths: ["/saree-details"] },
  { key: "saree-barcodes", label: "Saree Barcodes",  group: "Retail",   paths: ["/saree-barcodes"] },
  { key: "excel-upload",   label: "Excel Upload",    group: "Retail",   paths: ["/excel-upload"] },
  { key: "gst-report",     label: "GST Report",      group: "Retail",   paths: ["/gst-report"] },
  { key: "faq",            label: "FAQ",             group: "Retail",   paths: ["/frequently-asked-questions"] },
  { key: "promo",          label: "Promocode",       group: "Retail",   paths: ["/promo-codes"] },
  { key: "ideas",          label: "Our Ideas",       group: "Retail",   paths: ["/explore-our-ideas"] },
  { key: "homepage",       label: "HomePage",        group: "Retail",   paths: ["/homepage"] },
  // Boutique
  { key: "boutique-dashboard", label: "Boutique Dashboard", group: "Boutique", paths: ["/boutique/dashboard", "/boutique/coming-soon"] },
  { key: "fabric-database",    label: "Fabric Database",    group: "Boutique", paths: ["/boutique/fabric-database"] },
  { key: "custom-printing",    label: "Custom Printing",    group: "Boutique", paths: ["/boutique/custom-printing"] },
  { key: "order-management",   label: "Order Management",   group: "Boutique", paths: ["/boutique/order-management"] },
  { key: "crm",                label: "CRM",                group: "Boutique", paths: ["/boutique/crm"] },
  { key: "analytics",          label: "Analytics",          group: "Boutique", paths: ["/boutique/analytics"] },
];

// Admin-only pages — never assignable to other roles
const ADMIN_ONLY = [
  { key: "users",     paths: ["/user-management"] },
  { key: "settings",  paths: ["/settings"] },
  { key: "audit-log", paths: ["/audit-log"] },
  { key: "approvals", paths: ["/product-approvals"] },
];

const ALL_KEYS      = MENU_ACCESS.map((m) => m.key);
const BOUTIQUE_KEYS = MENU_ACCESS.filter((m) => m.group === "Boutique").map((m) => m.key);

// Default menus per role (used when a user has no custom selection)
export const ROLE_DEFAULTS = {
  "admin-staff": ALL_KEYS,
  "staff":       ["dashboard", "products", "orders", ...BOUTIQUE_KEYS],
  "stock-staff": ["stock", "saree-details", "saree-barcodes", "excel-upload"],
};

export const isFullAccessRole = (role) => role === "admin" || role === "super-admin";

export const defaultPermissionsFor = (role) =>
  isFullAccessRole(role) ? ALL_KEYS : ROLE_DEFAULTS[role] || [];

// Menus the logged-in user may see
export const getPermissions = () => {
  const role = getRole();
  if (isFullAccessRole(role)) return [...ALL_KEYS, ...ADMIN_ONLY.map((m) => m.key)];
  try {
    const saved = JSON.parse(sessionStorage.getItem("adminPermissions"));
    // Only assignable menus count — admin-only pages can't be granted
    if (Array.isArray(saved)) return saved.filter((k) => ALL_KEYS.includes(k));
  } catch { /* fall through to role defaults */ }
  return defaultPermissionsFor(role);
};

export const canAccess = (key) => getPermissions().includes(key);

const findMenu = (pathname) =>
  [...MENU_ACCESS, ...ADMIN_ONLY].find((m) =>
    m.paths.some((p) => pathname === p || pathname.startsWith(p + "/"))
  );

// Pages not tied to a menu (e.g. update password) stay open
export const canAccessPath = (pathname) => {
  const menu = findMenu(pathname);
  return !menu || canAccess(menu.key);
};

export const hasBoutiqueAccess = () => getPermissions().some((k) => BOUTIQUE_KEYS.includes(k));
export const hasRetailAccess   = () => getPermissions().some((k) => !BOUTIQUE_KEYS.includes(k));

// First allowed page inside one mode ("retail" | "boutique")
export const getModeHomePath = (mode) => {
  const group = mode === "boutique" ? "Boutique" : "Retail";
  const first = MENU_ACCESS.find((m) => m.group === group && canAccess(m.key));
  return first ? first.paths[0] : null;
};

// First page the user is allowed to open — prefers Boutique dashboard, as login did before
export const getHomePath = () => {
  if (canAccess("boutique-dashboard")) return "/boutique/dashboard";
  const first = MENU_ACCESS.find((m) => canAccess(m.key));
  return first ? first.paths[0] : "/admin/login";
};
